import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import ScrollToTop from "@/components/layout/ScrollToTop";
import motion from "@/lib/lazy-motion-tags";
import { Helmet } from "react-helmet";
// icons are lazy-loaded via IconLoader
import { Card } from "@/components/ui/card";
import IconLoader from "@/components/IconLoader";

const WhyWPC = () => {
  const composition = [
    { percentage: "70%", material: "ألياف خشبية طبيعية" },
    { percentage: "20%", material: "بوليمرات بلاستيكية عالية الجودة" },
    { percentage: "10%", material: "مواد مضافة تحسن الأداء وتطيل العمر الافتراضي" },
  ];

  const comparisonData = [
    {
      criterion: "مقاومة الماء",
      wpc: { value: "ممتازة 100%", isGood: true },
      natural: { value: "ضعيفة - ينتفخ", isGood: false },
      mdf: { value: "متوسطة - يتأثر", isGood: false },
      aluminum: { value: "جيدة لكن يصدأ", isGood: false },
    },
    {
      criterion: "الصيانة",
      wpc: { value: "تنظيف بسيط فقط", isGood: true },
      natural: { value: "تحتاج صيانة دورية", isGood: false },
      mdf: { value: "تحتاج إعادة دهان كل 2-3 سنوات", isGood: false },
      aluminum: { value: "تحتاج تنظيف", isGood: false },
    },
    {
      criterion: "العمر الافتراضي",
      wpc: { value: "+25 سنة", isGood: true },
      natural: { value: "10-15 سنة", isGood: false },
      mdf: { value: "8-12 سنة", isGood: false },
      aluminum: { value: "15-20 سنة", isGood: false },
    },
    {
      criterion: "مقاومة الحشرات",
      wpc: { value: "مقاومة تامة 100%", isGood: true },
      natural: { value: "عرضة للنمل الأبيض", isGood: false },
      mdf: { value: "عرضة", isGood: false },
      aluminum: { value: "مقاومة", isGood: true },
    },
    {
      criterion: "العزل الصوتي",
      wpc: { value: "عالي جداً", isGood: true },
      natural: { value: "جيد", isGood: true },
      mdf: { value: "متوسط", isGood: false },
      aluminum: { value: "ضعيف", isGood: false },
    },
    {
      criterion: "صديقة للبيئة",
      wpc: { value: "قابلة للتدوير 100%", isGood: true },
      natural: { value: "تتطلب قطع أشجار", isGood: false },
      mdf: { value: "تحتوي على مواد كيميائية", isGood: false },
      aluminum: { value: "تتطلب طاقة عالية", isGood: false },
    },
  ];

  const benefits = [
    {
      title: "توفير المال",
      description: "رغم أن السعر قد يكون مشابه للخشب الطبيعي، إلا أن WPC يوفر على المدى الطويل. توفير 500-1000 ريال سنوياً للحاجة لإعادة الدهان.",
    },
    {
      title: "خيار صحي للعائلة",
      description: "خالية من المواد الكيميائية الضارة والفورمالديهايد الموجودة في MDF، مما يجعلها خياراً صحياً لمنزلك وأطفالك.",
    },
    {
      title: "وفر وقتك الثمين",
      description: "لا داعي لقضاء عطلة نهاية الأسبوع في صيانة الأبواب أو انتظار النجار. أبوابنا تحافظ على جمالها بأقل مجهود.",
    },
    {
      title: "مناسبة للمناخ السعودي",
      description: "المناخ في السعودية يتميز بالحرارة العالية والرطوبة في بعض المناطق، مما يجعل الخشب الطبيعي يتشقق ويتشوه. أبواب WPC مصممة لتحمل هذه الظروف دون أي تأثير.",
    },
  ];

  return (
    <>
      <ScrollToTop />
      <Helmet>
        <title>لماذا WPC - مقارنة مع الألومنيوم والخشب الطبيعي | New Business</title>
        <meta name="description" content="اكتشف مزايا أبواب WPC مقارنة مع MDF والألومنيوم. مقاومة للماء، لا تحتاج صيانة واستدامة أطول" />
        <meta name="keywords" content="لماذا WPC, أبواب WPC, مقارنة أبواب, خشب مركب, مقاومة للماء, لا تحتاج صيانة, استدامة, الرياض" />
        <meta property="og:title" content="لماذا WPC - مقارنة مع الألومنيوم والخشب الطبيعي | New Business" />
        <meta property="og:description" content="اكتشف مزايا أبواب WPC مقارنة مع MDF والألومنيوم. مقاومة للماء، لا تحتاج صيانة واستدامة أطول" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.newbusiness.com.sa/why-wpc" />
        <link rel="canonical" href="https://www.newbusiness.com.sa/why-wpc" />
      </Helmet>

      <div className="min-h-screen">
        <Navbar />

        {/* Hero */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-background via-muted/30 to-background">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                لماذا <span className="text-gradient">WPC</span>؟
              </h1>
              <p className="text-xl text-muted-foreground">
                تقنية حديثة تتفوق على جميع البدائل التقليدية
              </p>
            </motion.div>
          </div>
        </section>

        {/* What is WPC */}
        <section className="py-24 bg-card">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto text-center mb-16"
            >
              <h2 className="text-4xl font-bold mb-6">ما هي تقنية WPC؟</h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                WPC اختصار لـ Wood Plastic Composite أي الخشب البلاستيكي المركب، وهي تقنية حديثة تجمع بين مزايا الخشب الطبيعي والبلاستيك المقوى لإنتاج مادة فائقة الجودة تتفوق على كليهما.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              {composition.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="p-8 text-center hover-lift">
                    <div className="text-5xl font-bold text-gradient mb-4">{item.percentage}</div>
                    <p className="text-muted-foreground">{item.material}</p>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-24 bg-background">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold mb-6">جدول المقارنة الشامل</h2>
              <p className="text-xl text-muted-foreground">مقارنة موضوعية بين أبواب WPC والبدائل الأخرى</p>
            </motion.div>

            <div className="overflow-x-auto">
              <table className="w-full max-w-6xl mx-auto">
                <thead>
                  <tr className="bg-gradient-primary">
                    <th className="p-4 text-right text-accent font-bold">المعيار</th>
                    <th className="p-4 text-center text-accent font-bold">WPC</th>
                    <th className="p-4 text-center text-accent font-bold">الخشب الطبيعي</th>
                    <th className="p-4 text-center text-accent font-bold">MDF</th>
                    <th className="p-4 text-center text-accent font-bold">الألومنيوم</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.map((row, index) => (
                    <motion.tr
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className="border-b border-border hover:bg-muted/50 transition-colors"
                    >
                      <td className="p-4 font-bold">{row.criterion}</td>
                      <td className="p-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <IconLoader name="CheckCircle2" className="h-5 w-5 text-green-500 flex-shrink-0" />
                          <span className="text-sm">{row.wpc.value}</span>
                        </div>
                      </td>
                      <td className="p-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          {row.natural.isGood ? (
                            <IconLoader name="CheckCircle2" className="h-5 w-5 text-green-500 flex-shrink-0" />
                          ) : (
                            <IconLoader name="XCircle" className="h-5 w-5 text-red-500 flex-shrink-0" />
                          )}
                          <span className="text-sm">{row.natural.value}</span>
                        </div>
                      </td>
                      <td className="p-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          {row.mdf.isGood ? (
                            <IconLoader name="CheckCircle2" className="h-5 w-5 text-green-500 flex-shrink-0" />
                          ) : (
                            <IconLoader name="XCircle" className="h-5 w-5 text-red-500 flex-shrink-0" />
                          )}
                          <span className="text-sm">{row.mdf.value}</span>
                        </div>
                      </td>
                      <td className="p-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          {row.aluminum.isGood ? (
                            <IconLoader name="CheckCircle2" className="h-5 w-5 text-green-500 flex-shrink-0" />
                          ) : (
                            <IconLoader name="XCircle" className="h-5 w-5 text-red-500 flex-shrink-0" />
                          )}
                          <span className="text-sm">{row.aluminum.value}</span>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-24 bg-card">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold mb-6">
                لماذا WPC هو <span className="text-gradient">الخيار الأمثل</span>؟
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="p-8 hover-lift h-full">
                    <h3 className="text-2xl font-bold mb-4 text-gradient">{benefit.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{benefit.description}</p>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default WhyWPC;
